<template>
  <div>

    <!-- ── Full Data Export layout ── -->
    <template v-if="restrictToCompany">

      <!-- Recipients: always visible -->
      <div class="fde-section mb-4">
        <h6 class="fde-section__title">Recipients</h6>

        <el-select
          v-model="recipients"
          multiple
          filterable
          default-first-option
          placeholder="Search and select recipients from your company"
          v-loading="isFetchingMembers"
          class="cf-recipient-select"
          @change="onRecipientsChange"
        >
          <el-option
            v-for="member in memberOptions"
            :key="member.user_email"
            :label="member.user && (member.user.first_name || member.user.last_name)
              ? (member.user.first_name + ' ' + member.user.last_name).trim() + ' (' + member.user_email + ')'
              : member.user_email"
            :value="member.user_email"
          />
        </el-select>
      </div>

      <!-- Schedule: pill style matching New Report -->
      <div class="fde-section">
        <div class="btn-group-toggle mb-1 d-flex align-items-center" data-toggle="buttons">
          <button
            :class="{ active: showScheduler }"
            @click.prevent="showScheduler = !showScheduler"
            class="btn badge badge-pill mr-2 mb-2 text-capitalize"
          >
            Schedule
          </button>
          <!-- Clear / disable the schedule: same pill style as the Schedule
               toggle, never in the active (filled) state. -->
          <button
            v-if="showScheduler"
            @click.prevent="clearSchedule"
            class="btn badge badge-pill mr-2 mb-2 text-capitalize"
          >
            Clear Schedule
          </button>
        </div>
        <SaveReportWizardScheduler v-if="showScheduler" v-model="exportSchedule" class="mb-4" />
      </div>

    </template>

    <!-- ── Regular export layout (non-Full Data Export) ── -->
    <template v-else>
      <div class="btn-group-toggle mb-1 d-flex align-items-center" data-toggle="buttons">
        <button
          :class="{ active: showScheduler }"
          @click.prevent="showScheduler = !showScheduler"
          class="btn badge badge-pill mr-2 mb-2 text-capitalize"
        >
          Schedule Report
        </button>
        <!-- Clear / disable the schedule: same pill style as the Schedule
             toggle, never in the active (filled) state. -->
        <button
          v-if="showScheduler"
          @click.prevent="clearSchedule"
          class="btn badge badge-pill mr-2 mb-2 text-capitalize"
        >
          Clear Schedule
        </button>
      </div>
      <SaveReportWizardScheduler v-if="showScheduler" v-model="exportSchedule" class="mb-4" />
    </template>

    <!-- ── LimeIQ Advanced Fields modal (non-superuser Full Data Export) ── -->
    <b-modal
      id="custom-fields-modal"
      centered
      size="sm"
      hide-header-close
      content-class="bg-white"
      modal-class="cf-modal"
      body-class="py-1 px-0"
      footer-class="p-2 px-3"
      header-class="py-2 px-3 border-bottom"
    >
      <template #modal-header>
        <div class="d-flex align-items-center">
          <span class="cf-modal-title">LimeIQ Advanced Fields</span>
          <b-icon
            icon="info-circle"
            class="ml-1 cf-info-icon"
            v-b-tooltip.hover.right="'If you\'re interested in additional columns, contact LimeIQ at info@limeiq.com'"
          />
        </div>
      </template>

      <div class="cf-field-list">

        <!-- TPA Group Name: selectable, underwriter-only -->
        <div v-if="isUnderwriter" class="cf-field-row">
          <span class="cf-field-label">TPA Group Name</span>
          <label class="custom-toggle mb-0">
            <input type="checkbox" v-model="tpaGroupName" @change="onTpaGroupNameChange" />
            <span class="custom-toggle-slider rounded-circle"></span>
          </label>
        </div>

        <!-- Lead Underwriter (CV-12): selectable by every role; the same column as the Select
             Columns "Lead Underwriter" box, so it shows whatever was picked there. -->
        <div class="cf-field-row">
          <span class="cf-field-label">Lead Underwriter</span>
          <label class="custom-toggle mb-0">
            <input
              type="checkbox"
              :checked="leadUnderwriterSelected"
              @change="$emit('set-lead-underwriter', $event.target.checked)"
            />
            <span class="custom-toggle-slider rounded-circle"></span>
          </label>
        </div>

        <!-- Locked fields: Broker, Active BDX, FCP, Jurisdiction -->
        <div
          v-for="field in disabledFields"
          :key="field.value"
          class="cf-field-row cf-field-row--locked"
        >
          <span class="cf-field-label cf-field-label--muted">{{ field.label }}</span>
          <label class="custom-toggle mb-0">
            <input type="checkbox" disabled />
            <span class="custom-toggle-slider cf-locked-slider rounded-circle"></span>
          </label>
        </div>

        <!-- Follow Data: locked, at the bottom -->
        <div class="cf-field-row cf-field-row--locked">
          <span class="cf-field-label cf-field-label--muted">Follow Data</span>
          <label class="custom-toggle mb-0">
            <input type="checkbox" disabled />
            <span class="custom-toggle-slider cf-locked-slider rounded-circle"></span>
          </label>
        </div>

      </div>

      <template #modal-footer>
        <b-button class="btn-grey" @click="$bvModal.hide('custom-fields-modal')">Close</b-button>
      </template>
    </b-modal>

  </div>
</template>

<script>
import moment from 'moment'

import { DATE_FORMAT } from '@constants'
import { userMixin } from '@mixins'
import membersService from '@services/membersService'

import { Select, Option } from 'element-ui'
import SaveReportWizardScheduler from './components/SaveReportWizardScheduler.vue'

const COMPANY_RESTRICTION_MESSAGE =
  'Full Data Exports may only be shared with users within your organization. This export contains data from multiple market participants and cannot be sent to external recipients.'

export default {
  props: {
    groupBy: {
      type: Array,
      default: () => []
    },
    groupByBroker: Boolean,
    companyId: {
      type: [Number, String],
      default: null
    },
    restrictToCompany: {
      type: Boolean,
      default: false
    },
    initialRecipients: {
      type: Array,
      default: () => []
    },
    initialSchedule: {
      // Saved schedule (start/end date, frequency, day, time, tz) restored when
      // amending a template; null for a fresh export. Seeds exportSchedule so
      // the previously-entered End Date and other fields repopulate.
      type: Object,
      default: null
    },
    initialTpaGroup: {
      type: Boolean,
      default: false
    },
    // Whether columns_to_show currently holds the Lead Underwriter column (CV-12).
    leadUnderwriterSelected: {
      type: Boolean,
      default: false
    },
    isUnderwriter: {
      type: Boolean,
      default: false
    }
  },
  components: {
    'el-select': Select,
    'el-option': Option,
    SaveReportWizardScheduler
  },
  mixins: [userMixin],
  data() {
    return {
      showScheduler: false,
      showRecipients: this.restrictToCompany,
      tpaGroupName: this.initialTpaGroup,
      recipients: this.initialRecipients.slice(),
      memberOptions: [],
      isFetchingMembers: false,
      recipientError: '',
      disabledFields: [
        { value: 'broker',         label: 'Broker' },
        { value: 'active_bdx',     label: 'Active BDX' },
        { value: 'fcp',            label: 'FCP' },
        { value: 'jurisdiction',   label: 'Jurisdiction' },
      ],
      exportSchedule: {
        // Default the schedule to start today so the picker shows a date
        // rather than opening blank (Start Date is required).
        start_date: new Date(),
        end_date: null,
        occurrence: 'Daily',
        day_of_month: 'First',
        day_of_week: null,
        time_of_day: null,
        timezone: moment.tz.guess()
      },
      teaserFields: [
        { value: 'tpa_group_name',   label: 'TPA Group Name' },
        { value: 'lead_underwriter', label: 'Lead Underwriter' },
        { value: 'broker',           label: 'Broker' },
        { value: 'active_bdx',       label: 'Active BDX' },
        { value: 'fcp',              label: 'FCP' },
        { value: 'jurisdiction',     label: 'Jurisdiction' },
      ]
    }
  },
  created() {
    // Amend flow: seed the scheduler from the saved schedule BEFORE the child
    // Scheduler mounts, so previously entered values (esp. End Date) repopulate
    // instead of resetting. created() runs before any child render/mount.
    this.seedScheduleFromInitial()
  },
  async mounted() {
    if (!this.restrictToCompany) return
    const cid = this.companyId || this.userCompanyId
    await this._fetchMembers(cid)
  },
  methods: {
    // Populate exportSchedule + open the scheduler from a saved schedule
    // (initialSchedule) when amending a template. Dates arrive as strings from
    // the API and are parsed to Date objects the pickers expect; day_of_week is
    // normalised to an array for Weekly (checkbox) and a string for Monthly.
    seedScheduleFromInitial() {
      const s = this.initialSchedule
      if (!s || !s.occurrence) return

      const toDate = (v) => {
        if (!v) return null
        const m = moment(v, [moment.ISO_8601, 'YYYY-MM-DD', 'MM-DD-YYYY'])
        return m.isValid() ? m.toDate() : null
      }

      let dow = s.day_of_week
      if ('Weekly' === s.occurrence) {
        dow = Array.isArray(dow)
          ? dow
          : (typeof dow === 'string' && dow ? dow.split(',') : [])
      } else if ('Monthly' === s.occurrence) {
        dow = Array.isArray(dow) ? (dow[0] || null) : (dow || null)
      }

      this.exportSchedule = {
        start_date: toDate(s.start_date) || new Date(),
        end_date: toDate(s.end_date),
        occurrence: s.occurrence,
        day_of_month: s.day_of_month || 'First',
        day_of_week: dow,
        // time_of_day arrives as the stored "h:mm A" string; the Scheduler's
        // timeOfDaySlot getter accepts that form and maps it to Morning/Evening.
        time_of_day: s.time_of_day || null,
        timezone: s.timezone || moment.tz.guess()
      }
      // A saved schedule means scheduling was active — open the scheduler so the
      // restored values (incl. End Date) are visible for amending.
      this.showScheduler = true
    },
    // A fresh schedule with all fields at their defaults (matches the initial
    // data() shape). Used when the user clears the Schedule selection.
    defaultSchedule() {
      return {
        start_date: new Date(),
        end_date: null,
        occurrence: 'Daily',
        day_of_month: 'First',
        day_of_week: null,
        time_of_day: null,
        timezone: moment.tz.guess()
      }
    },
    // Clear / disable the Schedule selection: reset every field to its default
    // and turn the scheduler off, reverting the export to a one-time run. The
    // showScheduler watcher emits set-schedule-active(false) so the parent
    // re-enables the one-time export path.
    clearSchedule() {
      this.exportSchedule = this.defaultSchedule()
      this.showScheduler = false
    },
    onTpaGroupNameChange() {
      this.$emit('set-tpa-group-name', this.tpaGroupName)
    },
    async _fetchMembers(cid) {
      this.isFetchingMembers = true
      try {
        const response = await membersService.list(
          { company_id: cid || undefined, page_size: 1000 },
          false
        )
        const membersData = response.data.data.data && response.data.data.data.members
        const members = (membersData && membersData.results) || []
        this.memberOptions = members.filter((m) => m.user_email && m.is_active !== false)
        console.log(`[FDE] _fetchMembers cid=${cid} → ${this.memberOptions.length} members`)
      } catch (e) {
        console.error('[FDE] Failed to fetch company members:', e)
      }
      this.isFetchingMembers = false
    },
    onRecipientsChange(newValue) {
      if (!this.restrictToCompany) {
        this.$emit('set-recipients', newValue)
        return
      }
      const validEmails = new Set(this.memberOptions.map((m) => m.user_email))
      const filtered = newValue.filter((email) => validEmails.has(email))
      if (filtered.length < newValue.length) {
        this.recipientError = COMPANY_RESTRICTION_MESSAGE
        this.recipients = filtered
      } else {
        this.recipientError = ''
      }
      this.$emit('set-recipients', filtered)
    },
  },
  watch: {
    companyId(newId) {
      this._fetchMembers(newId)
    },
    // The amend fetch is async, so initialSchedule may arrive AFTER this
    // component was created. Re-seed when it lands so the End Date and other
    // saved values still repopulate.
    initialSchedule: {
      handler(val) {
        if (val && val.occurrence) this.seedScheduleFromInitial()
      }
    },
    // Tell the parent whether scheduling is active so it can disable the
    // "One Time Export" option while a scheduled export is being configured.
    showScheduler: {
      immediate: true,
      handler(active) {
        this.$emit('set-schedule-active', active)
      }
    },
    exportSchedule: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.$emit('set-export-schedule', {
          start_date: newValue.start_date
            ? moment(newValue.start_date).format(DATE_FORMAT)
            : null,
          end_date: newValue.end_date
            ? moment(newValue.end_date).format(DATE_FORMAT)
            : null,
          occurrence: newValue.occurrence,
          day_of_month:
            'Monthly' == newValue.occurrence ? newValue.day_of_month : null,
          day_of_week:
            ['Weekly', 'Monthly'].includes(newValue.occurrence) &&
            newValue.day_of_week
              ? Array.isArray(newValue.day_of_week)
                ? newValue.day_of_week.join(',')
                : newValue.day_of_week
              : null,
          // time_of_day may be a Date (fresh pick) or the stored "h:mm A"
          // string (restored on amend). Parse with explicit formats so a
          // restored string isn't treated as "Invalid date" and re-emitted as
          // such (which crashes the backend's strptime('%I:%M %p')).
          time_of_day: (() => {
            const t = newValue.time_of_day
            if (!t) return null
            const m = typeof t === 'string'
              ? moment(t, ['h:mm A', moment.ISO_8601])
              : moment(t)
            return m.isValid() ? m.format('h:mm A') : null
          })(),
          timezone: newValue.timezone
        })
      }
    }
  }
}
</script>

<style lang="scss">
.cf-modal .modal-dialog {
  max-width: 400px;
}
</style>

<style lang="scss" scoped>
/* ── LimeIQ Advanced Fields modal ── */

.cf-modal-title {
  font-size: 0.85rem;
  font-weight: 700;
  color: #1c4a50;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.cf-info-icon {
  font-size: 0.85rem;
  color: #6c757d;
  cursor: pointer;

  &:hover { color: #1c4a50; }
}

.cf-field-list {
  border-top: 1px solid #e9ecef;
}

.cf-field-row {
  display: flex;
  align-items: center;
  padding: 0.55rem 1rem;
  border-bottom: 1px solid #f0f2f5;

  &--locked {
    opacity: 0.45;
    pointer-events: none;
  }
}

/* Lock icon in the white right-hand area of the pill (not on the thumb) */
.cf-locked-slider::after {
  content: '' !important;
  display: block !important;
  position: absolute !important;
  right: 5px !important;
  top: 50% !important;
  transform: translateY(-50%) !important;
  width: 12px !important;
  height: 12px !important;
  min-width: 0 !important;
  margin: 0 !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23495057'%3E%3Cpath d='M8 1a2 2 0 0 1 2 2v4H6V3a2 2 0 0 1 2-2zm3 6V3a3 3 0 0 0-6 0v4a2 2 0 0 0-2 2v5a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z'/%3E%3C/svg%3E") !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
  background-size: 10px !important;
}

.cf-field-label {
  font-size: 0.875rem;
  color: #1c4a50;
  font-weight: 600;
  flex: 1;

  &--muted {
    color: #495057;
    font-weight: 400;
  }
}

/* ── Full Data Export layout ── */

.fde-section__title {
  font-size: 0.9rem;
  font-weight: 700;
  color: #1c4a50;
  margin-bottom: 0.5rem;
}


.cf-recipient-select {
  width: 100%;
  max-width: 30rem;
}


/* ── Legacy styles (kept for non-Full Data Export path) ── */

.cf-btn-lock {
  font-size: 0.625rem;
}

.cf-pills {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
}

.cf-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
  border-radius: 10rem;
  background: #f1f3f5;
  border: 1px solid #dee2e6;
  color: #6c757d;
  user-select: none;
  letter-spacing: 0.01em;

  .cf-lock {
    font-size: 0.55rem;
    color: #adb5bd;
    flex-shrink: 0;
  }
}
</style>
